/**
 * SDK Bridge - Handles communication between the widget app and the parent SDK
 */

import { z } from 'zod';
import { getNativeApp, postToNativeApp } from './utils/native-app';

// Zod schemas - only validate required fields, accept anything for optional
export const InitPayloadSchema = z.object({
  organizationId: z.string(),
  agentId: z.string(),
  agentVariables: z.record(z.string(), z.unknown()),
  metadata: z.any().optional(),
  secureMetadata: z.any().optional(),
  defaultView: z.any().optional(),
  useCustomParent: z.any().optional(),
  darkMode: z.any().optional(),
  safeAreaInsets: z.any().optional(),
  keyboardHeight: z.any().optional()
});

export const WidgetOpenedPayloadSchema = z.object({
  agentId: z.string().optional(), // For switching agents via open()
  agentVariables: z.record(z.string(), z.unknown()),
  metadata: z.any().optional(),
  secureMetadata: z.any().optional(),
  safeAreaInsets: z.any().optional()
});

type MessageType =
  | 'STRADA_READY'
  | 'STRADA_INIT'
  | 'STRADA_SET_METADATA'
  | 'STRADA_SET_DARK_MODE'
  | 'STRADA_EVENT'
  | 'STRADA_REQUEST_USER_TOKEN'
  | 'STRADA_USER_TOKEN_RESPONSE'
  | 'STRADA_WIDGET_OPENED'
  | 'STRADA_KEYBOARD_HEIGHT'
  | 'STRADA_NEW_CHAT';

type IframeMessage = {
  type: MessageType;
  payload?: unknown;
};

type InitPayload = {
  organizationId: string;
  agentId: string;
  agentVariables: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  secureMetadata?: Record<string, unknown>;
  defaultView?: 'chat' | 'list';
  safeAreaInsets?: {
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
  };
  keyboardHeight?: number;
  useCustomParent?: boolean; // True when parentElement is provided - prevents responsive breakpoints
  darkMode?: boolean;
};

class SDKBridge {
  private isInIframe: boolean;
  private listeners: Map<MessageType, Array<(payload: unknown) => void>> = new Map();

  constructor() {
    this.isInIframe = window.self !== window.top;
    this.setupMessageListener();
  }

  /**
   * Check if running inside an iframe (embedded mode) or a native app (React Native or iOS SDK)
   */
  isEmbedded(): boolean {
    return this.isInIframe || getNativeApp() !== null;
  }

  /**
   * Send a message to the parent window or native app
   */
  sendToParent(type: MessageType, payload?: unknown): void {
    if (!this.isEmbedded()) {
      return;
    }

    const message: IframeMessage = { type, payload };

    // Native app (React Native or iOS SDK)
    if (getNativeApp()) {
      postToNativeApp(JSON.stringify(message));
    }
    // Regular iframe
    else if (this.isInIframe && window.parent) {
      window.parent.postMessage(message, '*');
    }
  }

  /**
   * Send ready message to parent
   */
  sendReady(): void {
    this.sendToParent('STRADA_READY');
  }

  /**
   * Send event to parent
   */
  sendEvent(eventKey: string, data: unknown): void {
    this.sendToParent('STRADA_EVENT', { eventKey, data });
  }

  /**
   * Request user token from SDK
   * Returns a promise that resolves when the SDK responds with the token
   */
  async requestUserToken(): Promise<string | null> {
    if (!this.isEmbedded()) {
      return null;
    }

    return new Promise((resolve) => {
      const handleResponse = (payload: unknown) => {
        const token = (payload as { token: string | null }).token;
        this.off('STRADA_USER_TOKEN_RESPONSE', handleResponse);
        resolve(token);
      };

      this.on('STRADA_USER_TOKEN_RESPONSE', handleResponse);
      this.sendToParent('STRADA_REQUEST_USER_TOKEN');

      // Timeout after 5 seconds
      setTimeout(() => {
        this.off('STRADA_USER_TOKEN_RESPONSE', handleResponse);
        resolve(null);
      }, 5000);
    });
  }

  /**
   * Register a listener for a specific message type
   */
  on(type: MessageType, callback: (payload: unknown) => void): void {
    if (!this.listeners.has(type)) {
      this.listeners.set(type, []);
    }
    this.listeners.get(type)!.push(callback);
  }

  /**
   * Remove a listener
   */
  off(type: MessageType, callback: (payload: unknown) => void): void {
    const callbacks = this.listeners.get(type);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Setup message listener for parent communication
   */
  private setupMessageListener(): void {
    const handler = (event: MessageEvent) => {
      // In a real implementation, you should validate the origin
      // if (event.origin !== 'https://expected-parent-domain.com') return;

      let message: IframeMessage;
      try {
        message = typeof event.data === 'string' ? JSON.parse(event.data) : (event.data as IframeMessage);
      } catch (error) {
        console.error('[SDKBridge] Error parsing message:', error);
        return;
      }

      if (!message || !message.type) {
        return;
      }

      // Trigger registered listeners
      const callbacks = this.listeners.get(message.type);
      if (callbacks) {
        callbacks.forEach((callback) => callback(message.payload));
      }
    };

    window.addEventListener('message', handler as EventListener);
    document.addEventListener('message', handler as EventListener);
  }
}

// Export singleton instance
export const sdkBridge = new SDKBridge();
export type { InitPayload };
